import monitoring from './monitoring';

export class WidgetError extends Error {
	static errorName = 'WidgetError';

	constructor(message, details = {}) {
		super(message);

		// Preserve prototype chain for older browsers
		Object.setPrototypeOf(this, new.target.prototype);

		// Ensure error name survives minification
		this.name = this.constructor.errorName;

		this.details = details;
	}

	addContext(context) {
		Object.assign(this.details, context);
		return this;
	}

	propagate() {
		monitoring.track(this, this.details, this.state);
		return this;
	}

	throw() {
		throw this;
	}

	serialize() {
		const { message, name, code, details, state } = this;
		return { message, name, code, details, state };
	}

	is(errorClass) {
		return this instanceof errorClass;
	}
}
export class InternalError extends WidgetError {
	static errorName = 'InternalError';
}
export class RuntimeError extends InternalError {
	static errorName = 'RuntimeError';
}

export class InitializationError extends InternalError {
	static errorName = 'InitializationError';
	state = 'initialization';
}

export class MissingCustomerError extends InternalError {
	static errorName = 'MissingCustomerError';
	state = 'missing_customer';
}

export class VatUpdateError extends InternalError {
	static errorName = 'VatUpdateError';
	state = 'vat_update';
}

export class BillingDetailsUpdateError extends InternalError {
	static errorName = 'BillingDetailsUpdateError';
	state = 'billing_details_update';
}

export class PaymentMethodVerificationError extends InternalError {
	static errorName = 'PaymentMethodVerificationError';
	state = 'payment_method_verification';
}

export const ErrorPaymentMethodType = Object.freeze({
	card: 'card',
	direct_debit: 'direct_debit',
	su_payouts: 'su_payouts',
	su_payment_methods: 'su_payment_methods',
	su_business_account: 'su_business_account'
});

export class PurchaseSubscriptionError extends InternalError {
	static errorName = 'PurchaseSubscriptionError';

	constructor(type, message, details = {}) {
		if (!Object.prototype.hasOwnProperty.call(ErrorPaymentMethodType, type)) {
			throw new InternalError(`Invalid payment method type: ${type}`);
		}
		super(message, details);
		this.state = `purchase.${ErrorPaymentMethodType[type]}`;
	}
}

export class ChangePaymentDetailsError extends InternalError {
	static errorName = 'ChangePaymentDetailsError';

	constructor(type, message, details = {}) {
		if (!Object.prototype.hasOwnProperty.call(ErrorPaymentMethodType, type)) {
			throw new InternalError(`Invalid payment method type: ${type}`);
		}
		super(message, details);
		this.state = `change_payment_details.${ErrorPaymentMethodType[type]}`;
	}
}

export class ApiError extends WidgetError {
	static errorName = 'ApiError';

	constructor(message, details = {}) {
		super(message);

		const { url, method, status, data = {} } = details;

		this.details = {
			url: hoistQueryParams(url, ['token']),
			method,
			status,
			data,
		};
	}

	isNetworkError() {
		return isNaN(this.details.status) || this.details.status === 0;
	}
	isHTTPBadRequest() { return this.details.status === 400; }
	isHTTPUnauthorized() { return this.details.status === 401; }
	isHTTPForbidden() { return this.details.status === 403; }
	isHTTPNotFoundError() { return this.details.status === 404; }
	isHTTPConflict() { return this.details.status === 409; }
	isHTTPBadGateway() { return this.details.status === 502; }
	isHTTPServiceUnavailable() { return this.details.status === 503; }
	isHTTPGatewayTimeout() { return this.details.status === 504; }
}

export class BillingError extends ApiError {
	static errorName = 'BillingError';

	get customerHasSubscription() {
		return this.details.data?.code === 'customerHasActiveSubscription';
	}

	get hasInvalidPayload() {
		return this.details.data?.code === 'schema';
	}
}

export class SumUpError extends ApiError {
	static errorName = 'SumUpError';
	state = 'sumup-api';

	get hasInvalidCardData() {
		return this.details.data?.error_code === 'PAYMENT_INSTRUMENT_VALIDATION';
	}
}

export class ViesError extends ApiError {
	static errorName = 'ViesError';
}

export class EPayError extends ApiError {
	static errorName = 'EPayError';

	constructor(message, details = {}) {
		super(message, details);
		this.errorCode = details?.data?.error;
	}
}

export class SDKError extends WidgetError {
	static errorName = 'SDKError';
}

export class SumupSubscriptionsError extends ApiError {
	static errorName = 'SumupSubscriptionsError';

	constructor(message, details) {
		super(message, details);
		this.state = 'sumup-subscriptions-error';
		this.errorCode = details?.data?.error?.code;
	}

	get isMaxQuantityExceeded() {
		return ['SM-SB-SPCH-206', 'SM-SB-SPRV-110'].includes(this.errorCode);
	}
}

export class MerchantWalletError extends ApiError {
	static errorName = 'MerchantWalletError';

	constructor(message, details) {
		super(message, details);
		this.state = 'merchant-wallet-error';
		this.errorCode = details?.data?.code;
	}
}

function hoistQueryParams(url, sensitiveData = []) {
	if (typeof url === 'string') {
		let output = url;
		for (const param of sensitiveData) {
			output = output.replace(new RegExp(`${param}=[^&]+`, 'g'), `${param}=***`);
		}
		return output;
	}
	return url;
}
